import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button } from '@mui/material';
import {
  GridCallbackDetails,
  GridFilterModel,
  GridRowParams,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { MasterIndexApi } from '@/api';
import { ConfirmationDialog, DataGrid, Page } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { IMasterNamePerson, Role } from '@/models';
import { MasterIndexActionsMenu } from '@/pages/MasterIndex/components/MasterIndexActionsMenu';
import { useMasterIndexRowMenu } from '@/pages/MasterIndex/hooks/useMasterIndexRowMenu';
import PersonPrintModal from '@/pages/MasterIndex/Persons/reports/PersonPrintModal';
import { usePersonPrint } from '@/pages/MasterIndex/Persons/reports/usePersonPrint';
import { isAdmin, isTargetRole } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface MasterPersonsDataGridProps {
  name: string;
  dataGridCols: DataGridColSettings[];
  onRowClick?: (
    params: GridRowParams,
    event: MuiEvent<React.MouseEvent>,
    details: GridCallbackDetails,
  ) => void;
  cta?: ReactNode;
  title?: string;
  verifiedOnly?: boolean;
  requireFilterForRecords?: boolean;
}

const MasterPersonsDataGrid = ({
  name,
  dataGridCols,
  onRowClick,
  cta,
  title = 'Master Index Persons',
  verifiedOnly = false,
  requireFilterForRecords,
}: MasterPersonsDataGridProps) => {
  const { user, userAgencyId } = useUserStore();
  const { updateToast } = useToastStore();
  const navigate = useNavigate();
  const { navigateWithSearch } = useSearchPersist();
  const { printPersonId, print, closePrint } = usePersonPrint();
  const [selectedId, setSelectedId] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isNameFilterActive, setIsNameFilterActive] = useState<boolean>(false);
  const {
    anchorEl,
    selectedItem: selectedPerson,
    handleOpenMenu,
    handleCloseMenu,
  } = useMasterIndexRowMenu<IMasterNamePerson>();
  const canDeleteMasterIndex = Boolean(
    user &&
      ((userAgencyId && isAdmin(user, userAgencyId)) ||
        isTargetRole(user, Role.ACCOUNT_ADMIN)),
  );

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `${name}-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return dataGridCols;
      }

      return mergeColumnSettings(dataGridCols, savedSettings);
    },
  );

  const handleColumnSettingsUpdate = (newSettings: DataGridColSettings[]) => {
    setColumnSettings(newSettings);

    if (user?._id && name) {
      localStorage.setItem(
        `${name}-${LocalStoragePrefix.TableSetting}-${String(user._id)}`,
        JSON.stringify(newSettings),
      );
    }
  };

  const fetchMasterPersons = useCallback(
    async (
      page = 1,
      limit = 100,
      filter = '',
    ): Promise<{ rows: any[]; totalCount?: number; hasNextPage?: boolean }> => {
      try {
        let response;
        if (verifiedOnly) {
          response = await MasterIndexApi.getVerifiedMasterPersons({
            page,
            limit,
            filter,
            sort: JSON.stringify({ createdAt: -1 }),
          });
        } else {
          response = await MasterIndexApi.getMasterPersons({
            page,
            limit,
            filter,
            sort: JSON.stringify({ lastName: 1 }),
          });
        }

        const { data } = response;
        const rows = 'results' in data ? data.results : data.docs;
        return {
          rows,
          totalCount: !data.hasNextPage
            ? (page - 1) * limit + rows.length
            : undefined,
          hasNextPage: data.hasNextPage,
        };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], hasNextPage: false };
      }
    },
    [updateToast, requireFilterForRecords, verifiedOnly],
  );

  const onDelete = (row: IMasterNamePerson) => {
    if (row._id === undefined) return;
    setSelectedId(row._id);
    setShowConfirm(true);
  };

  const handleDelete = async (row: string) => {
    if (row === undefined) return;
    try {
      await MasterIndexApi.deleteMasterPerson(row);
      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: 'Person deleted successfully',
      });
      setRefreshKey((prevState) => prevState + 1);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    if (!model) return;

    const safeModel = {
      ...model,
      items: Array.isArray(model.items) ? model.items : [],
    };

    const hasNameFilter = safeModel.items.some(
      (item) =>
        item &&
        (item.field === 'name' ||
          item.field === 'fullName' ||
          item.field === 'firstName' ||
          item.field === 'lastName') &&
        Boolean(item.value),
    );

    const hasQuickFilter = Boolean(
      safeModel.quickFilterValues && safeModel.quickFilterValues.length > 0,
    );

    setIsNameFilterActive(Boolean(hasNameFilter || hasQuickFilter));
  };

  const columns = useDataGridColumns({
    type: 'masterPersons',
    columnSettings,
    navigate,
    onDelete,
    openMenu: handleOpenMenu,
    isNameFilterActive,
  });

  // Create default row click handler if none is provided
  const handleRowClick = useCallback<
    NonNullable<MasterPersonsDataGridProps['onRowClick']>
  >(
    (params, event, details) => {
      // If onRowClick is provided from props, use that
      if (onRowClick) {
        onRowClick(params, event, details);
        return;
      }

      // Otherwise, handle internally - prevent navigation if clicking on buttons
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === 'BUTTON' ||
          event.target.closest('button') ||
          event.target.closest('[role="button"]') ||
          event.target.closest('a'))
      ) {
        return;
      }

      // Navigate to view page, preserving search query for back navigation
      if (params.row._id) {
        navigateWithSearch(`${params.row._id}/view`);
      }
    },
    [navigateWithSearch, onRowClick],
  );

  return (
    <Page title={title}>
      <Box
        height="inherit"
        paddingTop="14px"
        sx={{ height: 'calc(100vh - 150px)', overflow: 'auto' }}
      >
        <DataGrid
          key={refreshKey}
          name={name}
          fetchData={fetchMasterPersons}
          columnSettings={columnSettings}
          setColumnSettings={handleColumnSettingsUpdate}
          columns={columns}
          hideFilter
          persistSearch
          disableColumnSorting
          onFilterModelChange={handleFilterModelChange}
          cta={
            cta || (
              <Button
                onClick={() => {
                  navigate('new');
                }}
                color="error"
                variant="contained"
                size="large"
              >
                Add person
              </Button>
            )
          }
          onRowClick={handleRowClick}
          requireFilterForRecords={requireFilterForRecords}
        />
        <MasterIndexActionsMenu
          anchorEl={anchorEl}
          selectedItem={selectedPerson}
          onClose={handleCloseMenu}
          actions={[
            {
              key: 'view',
              label: 'View',
              onSelect: (person) => {
                if (person._id) {
                  navigateWithSearch(`${person._id}/view`);
                }
              },
            },
            {
              key: 'edit',
              label: 'Edit',
              onSelect: (person) => {
                if (person._id) {
                  navigateWithSearch(`${person._id}/edit`);
                }
              },
            },
            {
              key: 'print',
              label: 'Print',
              onSelect: (person) => {
                if (person._id) print(person._id);
              },
            },
            {
              key: 'delete',
              label: 'Delete',
              destructive: true,
              visible: canDeleteMasterIndex,
              onSelect: (person) => {
                onDelete(person);
              },
            },
          ]}
        />
        <ConfirmationDialog
          title="Confirm Delete Person Record"
          description="Are you sure you want to permanently delete this person from the records? This action cannot be undone and will remove all related information from the system."
          confirmText="Delete"
          open={showConfirm}
          onCancel={() => {
            setShowConfirm(false);
            setSelectedId('');
          }}
          onConfirm={() => {
            if (selectedId) handleDelete(selectedId);
            setShowConfirm(false);
          }}
        />
        <PersonPrintModal personId={printPersonId} onClose={closePrint} />
      </Box>
    </Page>
  );
};

export default MasterPersonsDataGrid;
